<template>
    <div class="file-chip">
        <e-taglist attached>
            <e-tag
                v-if="showLeftTag"
                :closable="closable"
                attached
                size="is-medium"
                aria-close-label="Remove file"
                @close="$emit('delete', file)"
            >
                <span v-if="customDownload && title" class="tw:truncate">{{ title }}</span>

                <span v-else-if="fileName" class="tw:truncate">{{ fileName }}</span>
                <span v-else>File</span>
                <template v-if="fileSize">
                    &nbsp;
                    <small>{{ fileSize }}</small>
                </template>
            </e-tag>
            <e-tag v-if="file.public_url || title" size="is-medium" attached>
                <a
                    :href="file.public_url"
                    class="tag has-text-weight-bold is-clickable tw:bg-transparent tw:bg-[var(--p-tag-primary-background)]!"
                    @click="download"
                >
                    <e-icon v-if="downloadStatus === 'downloaded'" icon="check-circle" size="is-small" type="is-primary" />
                    <e-icon v-if="downloadStatus === 'none'" icon="download-alt" size="is-small" type="is-primary" />
                    <spinner v-if="downloadStatus === 'downloading'" size="20" />
                </a>
            </e-tag>
        </e-taglist>
    </div>
</template>

<script>
import prettyBytes from 'pretty-bytes';

import Spinner from '@/components/chat/system/Spinner.vue';
import DownloadStatuses from '@/mixins/DownloadStatuses.mixin.js';

export default {
    name: 'FileChip',

    components: { Spinner },

    mixins: [DownloadStatuses],

    props: {
        file: {
            type: [Object, File],
            required: true,
        },

        canClose: {
            type: Boolean,
            default: false,
        },

        customDownload: {
            type: Boolean,
            default: false,
        },

        downloadStatus: {
            type: String,
            required: false,
            default: 'none',
        },

        showLeftTag: {
            type: Boolean,
            required: false,
            default: true,
        },
    },

    emits: ['download', 'delete'],

    computed: {
        /**
         * @returns {boolean}
         */
        closable() {
            return this.canClose && this.$options.emits.includes('delete');
        },

        title() {
            if (!this.$store.state.SlideDecks[`${this.file.model_id}`]) {
                return null;
            }

            const raw = this.$store.getters[`SlideDecks/${this.file.model_id}/get`]('title');

            return this.truncateFileName(`${raw}.pdf`);
        },

        /**
         * Safely get file name from various possible properties
         * @returns {string|null}
         */
        fileName() {
            if (!this.file) {
                return null;
            }

            const name = this.file.name || this.file.fileName || this.file.originalName || this.file.title || null;

            return this.truncateFileName(name);
        },

        /**
         * Safely format file size if available
         * @returns {string|null}
         */
        fileSize() {
            if (!this.file || !this.file.size) {
                return null;
            }
            try {
                return this.prettyBytes(this.file.size);
            } catch (error) {
                return null;
            }
        },
    },

    methods: {
        prettyBytes,

        truncateFileName(name, maxLength = 35) {
            if (!name || name.length <= maxLength) {
                return name;
            }

            const ext = name.lastIndexOf('.') !== -1 ? name.slice(name.lastIndexOf('.')) : '';

            return `${name.slice(0, maxLength - 3 - ext.length)}...${ext}`;
        },

        download($event) {
            if (this.customDownload) {
                this.$emit('download');
                $event.preventDefault();
            }
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

// TODO: fix spacing jump from one to multiple
.file-chip {
    margin-bottom: $tag-gap !important;
    overflow: hidden;

    .e-tags {
        max-width: 100%;
        flex-wrap: nowrap;
    }

    .p-tag:first-child {
        min-width: 0;
        overflow: hidden;

        > span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}
</style>
